import React from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FormattedMessage, useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';

import SettingsContext from 'src/features/administration/context/SettingsContext';
import { PERMISSION } from 'src/features/auth';
import CellLinkWithIcon from 'src/features/membership/components/cell/CellLinkWIthIcon';
import BackButton from 'src/features/navigation/components/BackButton';
import { PATH } from 'src/features/navigation/utils/constants';
import { useGetPayStationOperation } from 'src/features/payment/api/useGetPayStationOperation';
import CardTransactionsTable from 'src/features/payment/components/tables/CardTransactionsTable';
import CashTransactionsTable from 'src/features/payment/components/tables/CashTransactionsTable';
import PayStationEventsTable from 'src/features/payment/components/tables/PayStationEventsTable';
import RecycleBoxTransfersTable from 'src/features/payment/components/tables/RecycleBoxTransfersTable';
import {
    payStationStageBadgeOptions,
    payStationStatusBadgeOptions,
    PayStationOperationType,
    payStationTypeBadgeOptions,
} from 'src/features/payment/utils/constants';
import { paymentsMessages } from 'src/features/payment/utils/messages';
import DashboardGeneralBlock from 'src/features/stay/components/dashboard/DashboardGeneralBlock';
import { visitorMessages } from 'src/features/visitor/utils/messages';
import useHasPermissions from 'src/hooks/useHasPermissions';
import { useParking } from 'src/providers/ParkingProvider';
import { GeneralBlock } from 'src/registry/components/blocks/GeneralBlock';
import {
    SectionBlock,
    SectionBlockHead,
    SectionBlockHeader,
    SectionBlockTitle,
} from 'src/registry/components/blocks/SectionBlock';
import { LinkWithIcon } from 'src/registry/components/navigation/LinkWithIcon';
import { BadgeOption } from 'src/registry/types/badges';
import { Badge } from 'src/registry/ui/Badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from 'src/registry/ui/Tabs';
import { useTitleStore } from 'src/stores/useTitleStore';
import { getFormattedDateTimeForDetailPanel } from 'src/utils/getFormattedDateTime';

const CustomBadge = ({ badgeOption }: { badgeOption: BadgeOption }) => {
    const { theme, icon, label } = badgeOption;

    return (
        <Badge theme={theme} className="w-fit max-w-72 sm:max-w-80 md:max-w-72 lg:max-w-80 xl:max-w-92 2xl:max-w-80">
            {icon && <FontAwesomeIcon icon={icon} />}
            <div className="truncate">
                <FormattedMessage {...label} />
            </div>
        </Badge>
    );
};

export default function OperationDashboard() {
    const { formatMessage, formatNumber } = useIntl();
    const settingsData = React.useContext(SettingsContext);

    const accessActiveStays = useHasPermissions([PERMISSION.ACCESS_ACTIVE_STAYS]);
    const accessPastStaysStays = useHasPermissions([PERMISSION.ACCESS_ACTIVE_STAYS]);
    const accessPayStationEvents = useHasPermissions([PERMISSION.ACCESS_PAY_STATION_EVENTS]);
    const accessPayStationOperations = useHasPermissions([PERMISSION.ACCESS_PAY_STATION_OPERATIONS]);
    const accessPayStations = useHasPermissions([PERMISSION.ACCESS_PAY_STATIONS]);
    const accessPayments = useHasPermissions([PERMISSION.ACCESS_PAYMENTS]);

    const { operationId = '' } = useParams();

    const { setTitle, removeTitle } = useTitleStore();

    const { parkingId } = useParking();
    const { data } = useGetPayStationOperation({ parkingId, operationId });

    const operationType = data?.type;

    React.useEffect(() => {
        setTitle(formatMessage(paymentsMessages.operationDashboard));

        return removeTitle;
    }, [formatMessage, removeTitle, setTitle]);

    const activeStayId = data?.active_stay?.id;
    const pastStayId = data?.past_stay?.id;

    const urlPath = activeStayId
        ? `/${PATH.VISITORS}/active-stay/dashboard/${activeStayId}`
        : `/${PATH.VISITORS}/past-stay/dashboard/${pastStayId}`;

    const hasPermission = activeStayId ? accessActiveStays : accessPastStaysStays;
    const hasStayId = Boolean(activeStayId || pastStayId);

    const paymentDate = data?.payment_date ? getFormattedDateTimeForDetailPanel(data.payment_date) : '';
    const paymentFiscalNote = data?.fiscal_note_number ? data?.fiscal_note_number : '';
    const paymentDisplay = paymentDate && paymentFiscalNote ? `${paymentDate} #${paymentFiscalNote}` : paymentDate;
    const payment = data?.payment_id ? paymentDisplay : '-';

    const dashboardData = {
        section1: [
            {
                title: paymentsMessages.payStation,
                value: (
                    <CellLinkWithIcon
                        toPath={`/${PATH.PAYMENTS}/pay-station/${data?.pay_station?.id}`}
                        linkTitle={data?.pay_station?.name}
                        hasPermissions={accessPayStations}
                    />
                ),
            },
            {
                title: paymentsMessages.type,
                value: data ? <CustomBadge badgeOption={payStationTypeBadgeOptions[data?.type]} /> : '-',
            },
            {
                title: paymentsMessages.stage,
                value: data ? <CustomBadge badgeOption={payStationStageBadgeOptions[data?.stage]} /> : '-',
            },
            {
                title: paymentsMessages.status,
                value: data ? <CustomBadge badgeOption={payStationStatusBadgeOptions[data?.status]} /> : '-',
            },
            {
                title: paymentsMessages.stay,
                value: (
                    <LinkWithIcon
                        to={urlPath}
                        linkTitle={data?.active_stay?.ref ?? data?.past_stay?.ref ?? '-'}
                        hasPermissions={hasPermission && hasStayId}
                    />
                ),
            },
            {
                title: visitorMessages.amountDue,
                value: data?.requested_amount
                    ? formatNumber(data.requested_amount / 100, {
                          style: 'currency',
                          currency: data.currency ?? settingsData.default_currency,
                      })
                    : '-',
            },
            {
                title: paymentsMessages.paidAmount,
                value: data?.paid_amount
                    ? formatNumber(data.paid_amount / 100, {
                          style: 'currency',
                          currency: data.currency ?? settingsData.default_currency,
                      })
                    : '-',
            },
            {
                title: paymentsMessages.returnedAmount,
                value: data?.returned_amount
                    ? formatNumber(data.returned_amount / 100, {
                          style: 'currency',
                          currency: data.currency ?? settingsData.default_currency,
                      })
                    : '-',
            },
            {
                title: paymentsMessages.remainingAmountToReturn,
                value: data?.remaining_amount_to_return
                    ? formatNumber(data.remaining_amount_to_return / 100, {
                          style: 'currency',
                          currency: data.currency ?? settingsData.default_currency,
                      })
                    : '-',
            },
            {
                title: paymentsMessages.currency,
                value: data?.currency ?? settingsData.default_currency,
            },
            {
                title: paymentsMessages.deviceType,
                value: data?.device_type ?? '-',
            },
            {
                title: paymentsMessages.payment,
                value: data?.ref && (
                    <LinkWithIcon
                        to={`/${PATH.PAYMENTS}/payments?search=${data.ref}`}
                        linkTitle={payment}
                        hasPermissions={accessPayments && Boolean(data.payment_id)}
                    />
                ),
            },
        ],
    };

    const showTransactionsTab =
        operationType === PayStationOperationType.STAY_PAYMENT_CASH ||
        operationType === PayStationOperationType.EMPTY_RECYCLE_BOXES ||
        operationType === PayStationOperationType.STAY_PAYMENT_CARD;

    const defaultTabValue = accessPayStationOperations && showTransactionsTab ? 'transactions' : 'events';

    return (
        <>
            <BackButton customRoute={`/${PATH.PAYMENTS}/operation`} />

            <div className="flex flex-col gap-4 sm:gap-6 md:flex-row">
                <SectionBlock>
                    <SectionBlockHeader className="flex min-h-9 flex-row justify-between">
                        <SectionBlockHead>
                            <SectionBlockTitle>{data?.ref}</SectionBlockTitle>
                        </SectionBlockHead>
                    </SectionBlockHeader>
                    <GeneralBlock asChild className="flex-1 2xl:flex-2">
                        <DashboardGeneralBlock dashboardData={dashboardData} indexForLastSeparator={0} />
                    </GeneralBlock>
                </SectionBlock>
            </div>

            <Tabs defaultValue={defaultTabValue}>
                <TabsList>
                    {accessPayStationEvents && (
                        <TabsTrigger value="events">
                            <FormattedMessage {...paymentsMessages.eventLogs} />
                        </TabsTrigger>
                    )}
                    {accessPayStationOperations && showTransactionsTab && (
                        <TabsTrigger value="transactions">
                            {operationType === PayStationOperationType.STAY_PAYMENT_CASH && (
                                <FormattedMessage {...paymentsMessages.cashTransactions} />
                            )}
                            {operationType === PayStationOperationType.STAY_PAYMENT_CARD && (
                                <FormattedMessage {...paymentsMessages.cardTransactions} />
                            )}
                            {operationType === PayStationOperationType.EMPTY_RECYCLE_BOXES && (
                                <FormattedMessage {...paymentsMessages.recycleBoxTransfers} />
                            )}
                        </TabsTrigger>
                    )}
                </TabsList>
                {accessPayStationOperations && (
                    <TabsContent value="transactions">
                        {operationType === PayStationOperationType.STAY_PAYMENT_CASH && <CashTransactionsTable />}
                        {operationType === PayStationOperationType.STAY_PAYMENT_CARD && <CardTransactionsTable />}
                        {operationType === PayStationOperationType.EMPTY_RECYCLE_BOXES && <RecycleBoxTransfersTable />}
                    </TabsContent>
                )}
                {accessPayStationEvents && (
                    <TabsContent value="events">
                        <PayStationEventsTable />
                    </TabsContent>
                )}
            </Tabs>
        </>
    );
}
